import {
  Box,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  SimpleGrid,
} from '@chakra-ui/react';
import IconDotGrid1x3Vertical from 'central-icons/IconDotGrid1x3Vertical';
import mixpanel from 'mixpanel-browser';
import { useContext } from 'react';

import { BuildADashTrackingEvents, CenterSpinner } from '@revelio/core';

import { showAlertDialog } from '../../../../shared/components/alert-dialog';
import { CardListSelect } from '../../../../shared/ui/card-list';
import type { ReportPageConfig } from '../../../CustomDash/components/PageConfigProvider/types';
import { usePageManager } from '../../../CustomDash/components/PageManagerProvider/PageManagerProvider';
import { buildPathFromEntityType } from '../../../CustomDash/utils/routing-helpers';
import { CustomReportBuilderContext } from '../custom-report-builder-provider';
import { ReportSampleLink } from './report-sample-link';
import styles from './report-type.module.css';

export const CustomReportList = ({
  onReportTemplateChange,
}: {
  onReportTemplateChange: (report: ReportPageConfig | null) => void;
}) => {
  const {
    customReportsIsEnabled,
    customReportTemplates: selectableTemplates,
    isLoadingReportTemplates,
    selectedReportTemplate,
  } = useContext(CustomReportBuilderContext);
  const { deletePage } = usePageManager();

  const handleDeleteReport = (report: ReportPageConfig) => {
    showAlertDialog({
      alertType: 'confirm',
      isOpen: true,
      headerText: 'Are you sure you want to delete this report?',
      bodyContent: <span>This action is permanent and cannot be undone.</span>,
      footerAcceptIsDestructive: true,
      footerAcceptText: 'Yes, Delete',
      onAccept: () => {
        deletePage(report.id);
        mixpanel.track(BuildADashTrackingEvents.DELETE_PAGE, {
          page_id: report.id,
        });
        if (selectedReportTemplate?.id === report.id) {
          onReportTemplateChange(null);
        }
      },
    });
  };

  if (!customReportsIsEnabled) {
    return null;
  }

  if (isLoadingReportTemplates) {
    return (
      <Box mb="8">
        <CenterSpinner />
      </Box>
    );
  }

  if (selectableTemplates.length === 0) {
    return null;
  }

  return (
    <Box mb="8">
      <Flex align="center" justify="space-between" pb="3">
        <Heading
          as="h3"
          fontWeight="600"
          size="sm"
          color="text.primary"
          className={styles['sectionHeading']}
        >
          Custom Reports
        </Heading>
      </Flex>
      <SimpleGrid spacing="10px" columns={2} gridAutoFlow="row">
        {selectableTemplates.map((report) => (
          <CardListSelect<string>
            key={report.id}
            id={report.id}
            label={report.name}
            detail={report.tabs?.[0]?.label || ''}
            variant="report"
            isChecked={selectedReportTemplate?.id === report.id}
            onChange={() =>
              onReportTemplateChange(
                selectedReportTemplate?.id === report.id ? null : report
              )
            }
            cta={
              <Flex
                alignItems="center"
                gap={2}
                onClick={(e) => e.stopPropagation()}
              >
                {report.sampleFilterGroups ? (
                  <ReportSampleLink
                    link={buildPathFromEntityType('report', {
                      templateId: report.id,
                      search: `?${new URLSearchParams({
                        filterGroups: report.sampleFilterGroups,
                      }).toString()}`,
                    })}
                  />
                ) : null}
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<IconDotGrid1x3Vertical size={12} />}
                    size="sm"
                    aria-label="Actions"
                    variant="ghost"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Portal>
                    <MenuList>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteReport(report);
                        }}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </Portal>
                </Menu>
              </Flex>
            }
          />
        ))}
      </SimpleGrid>
    </Box>
  );
};
